import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { observer } from "mobx-react";
import * as Mousetrap from "mousetrap";

import { clone } from "@formant/core/common/clone";
import { defined } from "@formant/core/common/defined";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IFilter } from "@formant/core/model/IFilter";
import { ITags } from "@formant/core/model/ITags";
import { IView } from "@formant/core/model/IView";
import { LayoutType } from "@formant/core/model/LayoutType";
import { Uuid } from "@formant/core/model/Uuid";
import { Icon } from "@formant/ui/component/common/Icon";
import { WindowResizer } from "@formant/ui/component/common/WindowResizer";

import { track } from "../../../analytics";
import { useRouter } from "../../../context/RouterContext";
import { useStores } from "../../../context/StoreContext";
import { useOnClickOutside } from "../../../utils/hooks/useOnClickOutside";
import { useTimingCallbacks } from "../../../utils/hooks/useTimingCallbacks";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { CreateViewPopup } from "../../configuration/CreateViewPopup";
import {
    AddViewDropdown,
    DropdownButton,
    DropdownContainer,
    DropdownList,
    DropdownSideContainer,
    Item,
    ItemLabel,
    LongMenu,
    SideMenu,
    SideMenuDropdown,
    SideMenuIcon,
    StyledIcon,
    TabContainer,
} from "./styles";

interface ILabel {
    defaultTag?: boolean;
    id?: string;
    name?: string;
    icon?: string;
    layoutType?: LayoutType;
    url?: string | null | undefined;
    onClick?: (() => Promise<void>) | null | undefined;
}

interface IHomeTabsProps {
    context: IFilter;
    eventFilter: IEventFilter;
    value?: Uuid;
    device: {
        id: string;
        tags: ITags;
    } | null;
    filter: IFilter;
    filterActive: boolean;
    layoutType?: LayoutType;
    onEditDevice: (() => void) | null;
    onChangeViewId?: (id: Uuid) => void;

    views: IView[];
}

const SelectIcon = (icon?: string) => {
    switch (icon) {
        case "plus":
            return <StyledIcon name="plus" size="18" />;
        case "joystick":
            return <StyledIcon name="joystick" size="18" />;
        case "infinity":
            return <StyledIcon name="stream" size="18" />;
        case "board":
            return <StyledIcon name="dashboard" size="18" />;
        case "analytics":
            return <StyledIcon name="numeric" size="18" />;
        case "view":
            return <StyledIcon name="dashboard" size="18" />;
        case "bell":
            return <StyledIcon name="bell" size="18" />;
        case "calendar":
            return <StyledIcon name="calendar" size="18" />;
        default:
            return <StyledIcon name="dashboard" size="18" />;
    }
};

const getIconFromLayoutType = (type: LayoutType): string => {
    switch (type) {
        case "analytics":
            return "analytics";
        case "teleop":
            return "joystick";
        default:
            return "view";
    }
};

export const V2HomeTabs = observer(function HomeTabs(props: IHomeTabsProps) {
    const { value, device, views: sourceViews, onChangeViewId, filter } = props;
    const deviceId = device?.id;

    const { authStore, customModuleStore, observeStore } = useStores();

    const { navigate, routeTo } = useRouter();

    const navigatorEnabled = false;
    const hasTeleopViews = authStore.hasAccessByRole("operator");

    // modal
    const [creatingView, setCreatingView] = useState(false);
    const onCloseViewCreate = useCallback(() => {
        setCreatingView(false);
    }, [setCreatingView]);

    const onViewCreated = useCallback(
        async (id: Uuid) => {
            if (deviceId) {
                await navigate(
                    routeTo.unifiedDevice({
                        mode: "views",
                        id: defined(deviceId),
                        viewId: id,
                    }),
                );
            } else if (onChangeViewId) {
                onChangeViewId(id);
            }
            setCreatingView(false);
        },
        [setCreatingView, deviceId, navigate, routeTo],
    );

    const createView = useCallback(async () => {
        track("Header: Create View");
        setCreatingView(true);
    }, [setCreatingView, navigate, routeTo]);

    const createViewForMouseTrap = useCallback(
        (
            _e: Mousetrap.ExtendedKeyboardEvent,
            _combo: string,
        ): boolean | void => {
            track("Header: Create View");
            setCreatingView(true);
        },
        [setCreatingView, navigate, routeTo],
    );

    const fullscreenModules = (customModuleStore.listAll() || []).filter(
        _ => _.fullscreen,
    );

    const views = useMemo(() => {
        return sourceViews
            .filter(
                _ =>
                    !_.url || _.id === value || (_.url && _.showOnSingleDevice),
            )
            .filter(_ => {
                if (_.layoutType === "teleop") {
                    if (!navigatorEnabled) {
                        return false;
                    } else {
                        return hasTeleopViews;
                    }
                }
                return true;
            });
    }, [sourceViews]);

    const allMenuOptions = useMemo<ILabel[]>(() => {
        return [
            ...views.map(_ => ({
                defaultTag: false,
                id: _.id,
                name: _.name,
                icon: getIconFromLayoutType(_.layoutType || "observe"),
                url: _.url,
                onClick: undefined,
            })),
            ...(authStore.hasAccess("views", "administrator")
                ? [
                      {
                          defaultTag: true,
                          id: "add-view",
                          name: "",
                          icon: "plus",
                          onClick: createView,
                      },
                  ]
                : []),
        ];
    }, [views, authStore]);

    const [menuOptions, setMenuOptions] = useState<ILabel[]>(allMenuOptions);
    const [dropdownOptions, setDropdownOptions] =
        useState<ILabel[]>(allMenuOptions);
    const containerRef = useRef<HTMLUListElement>(null);
    const [hasDropdown, setHasDropdown] = useState(false);
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const [isClicked, setIsClicked] = useState(false);

    const handleResize = useCallback(() => {
        if (allMenuOptions.length > 1) {
            const aux = [];
            const dropdownAux = [];
            const totalSize = allMenuOptions.length * 160 + 60;
            const totalInterations =
                Math.floor(
                    ((containerRef?.current?.offsetWidth || 0) - 60) / 160,
                ) || 1;
            const temp = clone(allMenuOptions);
            aux.push(...temp.slice(0, totalInterations));
            dropdownAux.push(...temp.splice(totalInterations, temp.length));
            let selectedDropdown;
            for (let i = 0; i < dropdownAux.length; i++) {
                if (dropdownAux[i].id === value) {
                    [selectedDropdown] = dropdownAux.splice(i, 1);
                    break;
                }
            }
            if (selectedDropdown) {
                const removed = aux.pop();
                aux.push(selectedDropdown);
                if (removed) {
                    dropdownAux.unshift(removed);
                }
            }
            setMenuOptions(aux);
            setDropdownOptions(dropdownAux);
            if (totalSize >= (containerRef?.current?.offsetWidth || 0)) {
                setHasDropdown(true);
            } else {
                setHasDropdown(false);
            }
        } else {
            setMenuOptions(allMenuOptions);
        }
    }, [
        allMenuOptions,
        setDropdownIsOpen,
        setMenuOptions,
        setDropdownOptions,
        setHasDropdown,
    ]);

    const hidePopup = useCallback(() => {
        setDropdownIsOpen(false);
    }, [setDropdownIsOpen]);

    const debouncedResize = useTimingCallbacks({
        leadingFn: hidePopup,
        throttleFn: handleResize,
    });

    useEffect(() => handleResize(), [handleResize]);

    const dropdownRef = useOnClickOutside(
        useCallback(() => {
            setDropdownIsOpen(false);
        }, [setDropdownIsOpen]),
    );

    const changeActivePosition = (index: number) => {
        const auxMenu = [...(menuOptions ?? [])] as ILabel[];
        const auxDropdown = [...(dropdownOptions ?? [])] as ILabel[];
        const [selected] = auxDropdown.splice(index, 1);
        const replaced = auxMenu.pop();
        auxMenu.push(selected);
        if (replaced) {
            auxDropdown.unshift(replaced);
        }
        setMenuOptions(auxMenu);
        setDropdownOptions(auxDropdown);
    };

    // Side Dropdown
    const [mobileSideDropdownIcon, setMobileSideDropdownIcon] = useState(false);
    const mobileIconDropdownRef = useOnClickOutside(
        useCallback(
            () => setMobileSideDropdownIcon(false),
            [setMobileSideDropdownIcon],
        ),
    );

    useEffect(() => {
        const { userRole } = authStore;
        if (userRole && authStore.hasAccess("views", "administrator")) {
            Mousetrap.bind("shift+d", createViewForMouseTrap);
        }
    }, [authStore]);

    return (
        <>
            <WindowResizer onResize={debouncedResize} />

            <TabContainer>
                <LongMenu ref={containerRef} isOverflowing={dropdownIsOpen}>
                    {menuOptions?.map(e => {
                        if (e.id === "add-view") {
                            return (
                                <RestrictedComponent
                                    key={"navlink" + e.id}
                                    requiredAccess="administrator"
                                    resource="views"
                                >
                                    <a
                                        data-tooltip="Add a View"
                                        onClick={async () => await createView()}
                                    >
                                        <Item isActive={e.id === value}>
                                            <StyledIcon name="plus" size="18" />
                                        </Item>
                                    </a>
                                </RestrictedComponent>
                            );
                        }
                        return (
                            <a
                                data-tooltip={e.name}
                                key={"navlink" + e.id}
                                onClick={async () => {
                                    if (
                                        menuOptions.length === 1 &&
                                        e.id === value
                                    ) {
                                        if (!isClicked) {
                                            setIsClicked(true);
                                            setDropdownIsOpen(!dropdownIsOpen);
                                            setTimeout(() => {
                                                setIsClicked(false);
                                            }, 500);
                                        }
                                        return;
                                    } else if (
                                        authStore.hasAccessByRole(
                                            "administrator",
                                        ) &&
                                        e?.defaultTag &&
                                        e.onClick
                                    ) {
                                        return await e.onClick();
                                    }
                                    if (!device?.id && e?.id) {
                                        onChangeViewId?.(e.id);
                                        return;
                                    }
                                    if (device?.id) {
                                        return await navigate(
                                            `/devices/${device.id}/views/${e.id}`,
                                        );
                                    }
                                }}
                            >
                                <Item isActive={e.id === value}>
                                    {SelectIcon(e?.icon || "")}
                                    <ItemLabel type="h4">{e.name}</ItemLabel>
                                </Item>
                            </a>
                        );
                    })}
                    {hasDropdown && (
                        <DropdownContainer ref={dropdownRef}>
                            <DropdownButton
                                data-testid="open-header-dropdown"
                                onClick={() => {
                                    track("Header: Dropdown", {
                                        open: !dropdownIsOpen,
                                    });
                                    setDropdownIsOpen(!dropdownIsOpen);
                                }}
                            >
                                <StyledIcon name="next" />
                            </DropdownButton>

                            {dropdownIsOpen && (
                                <DropdownList>
                                    <RestrictedComponent
                                        requiredAccess="administrator"
                                        resource="views"
                                    >
                                        <AddViewDropdown
                                            onClick={createView}
                                            data-tooltip="Create a new View"
                                            data-testid="create-new-view"
                                        >
                                            <Item>
                                                <StyledIcon name="plus" />
                                                <ItemLabel
                                                    type="h4"
                                                    data-testid="add-view"
                                                >
                                                    Add View
                                                </ItemLabel>
                                            </Item>
                                            <span>
                                                <StyledIcon name="shift" />D
                                            </span>
                                        </AddViewDropdown>
                                    </RestrictedComponent>
                                    {dropdownOptions?.map((e, index) => {
                                        return e.id !== "add-view" &&
                                            device?.id ? (
                                            <a
                                                data-tooltip={e.name}
                                                key={"dropdown" + e.id}
                                                onClick={async () => {
                                                    changeActivePosition(index);
                                                    return await navigate(
                                                        `/devices/${device.id}/views/${e.id}`,
                                                    );
                                                }}
                                            >
                                                <Item isActive={e.id === value}>
                                                    {SelectIcon(e?.icon)}
                                                    <ItemLabel type="h4">
                                                        {e.name}
                                                    </ItemLabel>
                                                </Item>
                                            </a>
                                        ) : (
                                            ""
                                        );
                                    })}
                                </DropdownList>
                            )}
                        </DropdownContainer>
                    )}
                </LongMenu>

                <SideMenu>
                    <DropdownSideContainer ref={mobileIconDropdownRef}>
                        <SideMenuIcon
                            onClick={() => {
                                setMobileSideDropdownIcon(
                                    !mobileSideDropdownIcon,
                                );
                            }}
                        >
                            <Icon name="more" size="18" />
                        </SideMenuIcon>
                        {mobileSideDropdownIcon && (
                            <div
                                onClick={() => {
                                    setMobileSideDropdownIcon(false);
                                }}
                            >
                                <SideMenuDropdown>
                                    <RestrictedComponent
                                        requiredAccess="administrator"
                                        resource="views"
                                    >
                                        <a
                                            onClick={() => {
                                                observeStore.isCustomizing = true;
                                            }}
                                        >
                                            <li data-testid="customize-button">
                                                <StyledIcon
                                                    name="edit"
                                                    size="24"
                                                />
                                                <span>Customize</span>
                                            </li>
                                        </a>
                                    </RestrictedComponent>
                                    {device?.id && (
                                        <a
                                            onClick={() =>
                                                navigate(
                                                    `/devices/${device.id}/events`,
                                                )
                                            }
                                        >
                                            <li>
                                                <StyledIcon
                                                    name="bell"
                                                    size="24"
                                                />
                                                <span>Events</span>
                                            </li>
                                        </a>
                                    )}
                                </SideMenuDropdown>
                            </div>
                        )}
                    </DropdownSideContainer>
                </SideMenu>
            </TabContainer>

            {creatingView && (
                <CreateViewPopup
                    views={views}
                    modules={fullscreenModules}
                    onViewCreated={onViewCreated}
                    onCancel={onCloseViewCreate}
                    tags={device?.tags || {}}
                    deviceIds={
                        device?.id
                            ? [device?.id]
                            : [...(filter?.deviceIds || [])]
                    }
                />
            )}
        </>
    );
});
